import {
  Add as AddIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useWorkPanel } from "../../../../hooks/useWorkPanel";
import { Work } from "../../../../store/types/work.types";
import { WorkItemList, WorkItemStateEnum } from "../../../../store/types/work.types";
import ContactAttemptGroup from "./ContactAttemptGroup";
import StatusMark, { MarkStatus } from "./StatusMark";
import SubItem from "./SubItem";
import {
  AMBER,
  AMBER_BG,
  BG_SURFACE,
  DONE_BG,
  DONE_LINE,
  GREEN,
  GREEN_BG,
  INK,
  INK_3,
  INK_4,
  LANE_BODY_BG,
  LINE,
  LINE_2,
  ORANGE,
  PAPER,
  ROSE,
  ROSE_BG,
  ROSE_LINE,
  formatDateShort,
  getCaseRollup,
  groupContactAttempts,
} from "./types";

interface CaseLaneProps {
  work: Work;
  /** Samtliga barn från tidslinjens workitems-data — work.WorkItems från
   *  ärende-API:t saknar stängda aktiviteter och duger inte för rollup. */
  allChildren: WorkItemList[];
  visibleChildren: WorkItemList[];
  customerNo: number;
  isOpen: boolean;
  onToggle: () => void;
  onAddActivity: () => void;
  onCompleteActivity?: (item: WorkItemList) => void;
  futureFilterActive?: boolean;
}

const CLOSED_PER_LANE = 3;

const CaseLane = ({
  work,
  allChildren,
  visibleChildren,
  customerNo,
  isOpen,
  onToggle,
  onAddActivity,
  onCompleteActivity,
  futureFilterActive,
}: CaseLaneProps) => {
  const { openWork } = useWorkPanel();
  const [showAllClosed, setShowAllClosed] = useState(false);
  const isClosed = work.State === 2;

  const rollup = useMemo(() => getCaseRollup(allChildren), [allChildren]);

  const { upcomingChildren, closedChildren } = useMemo(() => {
    const upcoming: WorkItemList[] = [];
    const closed: WorkItemList[] = [];
    for (const child of visibleChildren) {
      if (child.State === WorkItemStateEnum.Closed) closed.push(child);
      else upcoming.push(child);
    }
    return { upcomingChildren: upcoming, closedChildren: closed };
  }, [visibleChildren]);

  const visibleClosed = showAllClosed
    ? closedChildren
    : closedChildren.slice(0, CLOSED_PER_LANE);
  const hiddenClosed = closedChildren.length - visibleClosed.length;

  const hasLate = !isClosed && rollup.overdue > 0;

  const markStatus: MarkStatus = isClosed
    ? "done"
    : rollup.done > 0
    ? "part"
    : "todo";

  const cardBorder = hasLate ? ROSE_LINE : isClosed ? DONE_LINE : LINE;
  const cardBg = isClosed ? DONE_BG : PAPER;
  const barColor = isClosed ? GREEN : ORANGE;
  const barPct = Math.round(
    (isClosed ? 1 : rollup.fraction) * 100
  );

  return (
    <Box
      sx={{
        bgcolor: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: "13px",
        overflow: "hidden",
      }}
    >
      {/* Head */}
      <Box
        onClick={() => openWork(work.Id)}
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr auto auto",
          gap: 1.5,
          alignItems: "center",
          p: "12px 14px",
          cursor: "pointer",
          "&:hover": { bgcolor: isClosed ? DONE_BG : BG_SURFACE },
          "@media (max-width: 600px)": {
            gridTemplateColumns: "auto 1fr auto",
            rowGap: 1,
          },
        }}
      >
        <StatusMark type="case" status={markStatus} fraction={rollup.fraction} />

        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: isClosed ? 500 : 600,
                color: isClosed ? INK_3 : INK,
                lineHeight: 1.3,
              }}
            >
              {work.Name}
            </Typography>
            {!isClosed && (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 1,
                  py: "1px",
                  borderRadius: 999,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: ".02em",
                  bgcolor: AMBER_BG,
                  color: AMBER,
                }}
              >
                Aktiv
              </Box>
            )}
            {hasLate && (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: "1px",
                  borderRadius: 999,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: ".02em",
                  bgcolor: ROSE_BG,
                  color: ROSE,
                }}
              >
                {rollup.overdue} försenad{rollup.overdue === 1 ? "" : "e"}
              </Box>
            )}
            {isClosed && (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 1,
                  py: "1px",
                  borderRadius: 999,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: ".02em",
                  bgcolor: GREEN_BG,
                  color: GREEN,
                }}
              >
                Klar
              </Box>
            )}
            {!isClosed && !hasLate && rollup.noDate > 0 && (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 1,
                  py: "1px",
                  borderRadius: 999,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: ".02em",
                  bgcolor: AMBER_BG,
                  color: AMBER,
                }}
              >
                {rollup.noDate} utan datum
              </Box>
            )}
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            gap={1.1}
            flexWrap="wrap"
            sx={{ mt: 0.5, fontSize: 12, color: INK_3 }}
          >
            {rollup.total > 0 && (
              <>
                <Box
                  sx={{
                    width: 78,
                    height: 5,
                    borderRadius: "3px",
                    bgcolor: LINE,
                    overflow: "hidden",
                    flex: "0 0 auto",
                  }}
                >
                  <Box
                    sx={{
                      width: `${barPct}%`,
                      height: "100%",
                      borderRadius: "3px",
                      bgcolor: barColor,
                    }}
                  />
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontWeight: isClosed ? 400 : 600,
                    color: isClosed ? INK_3 : "#3F3A36",
                  }}
                >
                  {rollup.done} av {rollup.total} klara
                </Box>
                <Box
                  sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: INK_4 }}
                />
              </>
            )}
            <Box component="span">
              {isClosed
                ? `Avslutad ${formatDateShort(work.Modified)}`
                : `Skapad ${formatDateShort(work.Created)}`}
            </Box>
            {work.Category?.Name && (
              <>
                <Box
                  sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: INK_4 }}
                />
                <Box component="span">{work.Category.Name}</Box>
              </>
            )}
          </Stack>
        </Box>

        {!isClosed && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon sx={{ fontSize: 12 }} />}
            onClick={(e) => {
              e.stopPropagation();
              onAddActivity();
            }}
            sx={{
              borderRadius: "9px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12,
              lineHeight: 1.2,
              px: 1.1,
              py: 0.4,
              minHeight: 0,
              whiteSpace: "nowrap",
              "& .MuiButton-startIcon": { mr: 0.5 },
              color: "#3F3A36",
              borderColor: LINE_2,
              bgcolor: PAPER,
              "&:hover": {
                borderColor: INK_4,
                bgcolor: PAPER,
                color: INK,
              },
              "@media (max-width: 600px)": {
                gridColumn: "1 / -1",
                justifySelf: "stretch",
              },
            }}
          >
            Aktivitet
          </Button>
        )}

        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          aria-label={isOpen ? "Fäll ihop" : "Visa aktiviteter"}
          sx={{
            color: INK_3,
            transition: "transform .2s",
            transform: isOpen ? "rotate(90deg)" : "rotate(0)",
            "&:hover": { color: INK, bgcolor: "rgba(0,0,0,.04)" },
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        {visibleChildren.length > 0 ? (
          <Box
            sx={{
              p: "9px 14px 12px",
              borderTop: `1px dashed ${LINE}`,
              bgcolor: LANE_BODY_BG,
            }}
          >
            {/* Streckad räls under ärendets markering, kopplar barnen visuellt. */}
            <Box
              sx={{
                borderLeft: `2px dashed ${LINE_2}`,
                ml: "9px",
                pl: "12px",
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
            {upcomingChildren.map((child) => (
              <SubItem
                key={`u-${child.Id}`}
                item={child}
                customerNo={customerNo}
                onComplete={onCompleteActivity}
              />
            ))}
            {groupContactAttempts(visibleClosed).map((seg, i) =>
              seg.kind === "attempts" ? (
                <ContactAttemptGroup
                  key={`ca-${seg.items[0].Id}`}
                  items={seg.items}
                  customerNo={customerNo}
                  variant="sub"
                />
              ) : (
                <SubItem
                  key={`c-${seg.item.Id ?? i}`}
                  item={seg.item}
                  customerNo={customerNo}
                  onComplete={onCompleteActivity}
                />
              )
            )}
            {hiddenClosed > 0 && (
              <Box sx={{ pt: 0.5 }}>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAllClosed(true);
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12,
                    color: ORANGE,
                    px: 0.5,
                    py: 0.25,
                    minHeight: 0,
                    "&:hover": { bgcolor: "rgba(255,95,0,.06)" },
                  }}
                >
                  Visa alla {closedChildren.length} tidigare aktiviteter
                </Button>
              </Box>
            )}
            {showAllClosed && closedChildren.length > CLOSED_PER_LANE && (
              <Box sx={{ pt: 0.5 }}>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAllClosed(false);
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: 12,
                    color: INK_3,
                    px: 0.5,
                    py: 0.25,
                    minHeight: 0,
                  }}
                >
                  Visa färre
                </Button>
              </Box>
            )}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              p: "12px 14px",
              borderTop: `1px dashed ${LINE}`,
              bgcolor: LANE_BODY_BG,
              color: INK_3,
              fontSize: 13,
              fontStyle: "italic",
            }}
          >
            Inga {futureFilterActive ? "kommande " : ""}aktiviteter på detta
            ärende.
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default CaseLane;
